import { useState } from 'react';
import Link from 'next/link';
import { useAuthentication } from '@context/businessContainer';
import Button from '@monofront/design-system/components/Button';
import Modal from '@monofront/design-system/organisms/Modal';
import Checkbox from '@monofront/design-system/components/Form/Checkbox';
import Label from '@monofront/design-system/components/Form/Label';
import { saveUserPreferences } from '@services/userPreferences';
import {
  ABTEST_UPDATE_PAYMENT_METHOD,
  PAYMENT_PREFERENCES_DOCUMENT_NAME,
} from '@monofront/business/constants';
import { usePaymentPreferences } from '@hooks/businessContainer';
import { useTranslations } from 'next-intl';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

interface IModalExpiringCardProps {
  onCardReplace?: () => void;
}

const ModalExpiringCard: React.FC<IModalExpiringCardProps> = ({
  onCardReplace,
}: IModalExpiringCardProps) => {
  const [doNotAskAgain, setDoNotAskAgain] = useState(false);
  const { customer } = useAuthentication();
  const { setIsExpiringCardModalOpen, isExpiringCardModalOpen } =
    usePaymentPreferences();
  const isExpiredCardBannerEnabled = useFeatureIsOn(
    ABTEST_UPDATE_PAYMENT_METHOD,
  );

  const handleCloseModal = () => {
    if (doNotAskAgain) {
      saveUserPreferences(customer!.uid, PAYMENT_PREFERENCES_DOCUMENT_NAME, {
        doNotAskAgainUpdateSubPM: doNotAskAgain,
      });
    }

    setIsExpiringCardModalOpen(false);
  };

  const tAccount = useTranslations('Account');

  if (!isExpiringCardModalOpen) {
    return null;
  }

  const handleReplaceCard = () => {
    if (onCardReplace && isExpiredCardBannerEnabled) {
      onCardReplace();
    }
    handleCloseModal();
  };

  return (
    <>
      <Modal
        isOpen={isExpiringCardModalOpen}
        externalToggle={handleCloseModal}
        UNSAFE_className="modal-expiring-card"
        hasPadding
      >
        <h2>{tAccount('creditCardExpiring')}</h2>
        <p>{tAccount('creditCardExpiringReplacement')}</p>
        <div className="do-not-ask-again">
          <Checkbox
            id="not-ask"
            name="newsletter"
            required={false}
            onClick={() => setDoNotAskAgain(!doNotAskAgain)}
          />
          <Label id="not-ask">{tAccount('dontAskAgain')}</Label>
        </div>
        <div className="modal-cta">
          <Button
            variant="secondary"
            hasMinWidth={false}
            onClick={handleCloseModal}
            dataTestId="ModalExpiringCard-Button-later"
          >
            {tAccount('later')}
          </Button>
          <Button hasMinWidth={false} onClick={handleReplaceCard}>
            {isExpiredCardBannerEnabled ? (
              tAccount('replace')
            ) : (
              <Link href={`/pages/manage-my-subscription`} legacyBehavior>
                <a
                  className="link-account"
                  data-testid="ModalExpiringCard-go-to-replace-card"
                >
                  {tAccount('replace')}
                </a>
              </Link>
            )}
          </Button>
        </div>
      </Modal>
      <style jsx>{`
        :global(.modal-expiring-card) {
          top: 33%;
          max-width: 560px;
        }

        h2 {
          margin-bottom: var(--space-3);
          font-weight: var(--font-weight-bold);
        }

        .modal-cta {
          margin-top: var(--space-5);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-cta > :global(button) {
          width: fit-content;
        }

        .modal-cta > :global(button:first-child) {
          margin-right: var(--space-3);
        }

        .do-not-ask-again {
          display: flex;
          align-items: center;
          margin-bottom: var(--space-3);
          margin-top: var(--space-4);
        }

        .do-not-ask-again > :global(label) {
          margin-left: var(--space-3);
          text-align: left;
        }
      `}</style>
    </>
  );
};

export default ModalExpiringCard;
